<template>
  <div ref="floatingComments" class="floating-comments-container">
    <ConversationCard
      v-for="comment of comments"
      :key="comment.id"
      :data-conversation-card-id="comment.id"
      :style="{ 'margin-top': getMarginTop(comment.id) + 'px' }"
      :margin-top="10"
      :comment="comment"
      :selected="isSelected(comment.id)"
      :hovered="comment.id === hoveredCommentId"
      :current-user="currentUser"
      :participants="participants"
      :is-room-owner="isRoomOwner"
      :read-only="readOnly"
      :all-active-participants="allActiveParticipants"
      @create-reply="$emit('create-reply', $event)"
      @select="$emit('select', comment.id)"
      @hover-change="$event => hoverComment($event ? comment.id : '')"
      @update="$emit('update', { ...$event, commentId: comment.id })"
      @delete="$emit('delete', comment.id)"
      @deselect="$emit('select', '')"
    />
  </div>
</template>
<script lang="ts">
import type { PropType, Ref } from 'vue';
import { nextTick, defineComponent, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute } from 'vue-router';
import type { CurrentUser } from '../types/current-user';
import type { Participant } from '../../types/participant';
import { useContextualCommentingStore } from '../store/contextual-commenting.store';
import type { Comment, CommentOffset } from '../types/comment';
import { useCommentsHeightsCalculator } from '../composables/comments-heights-calculator.composable';
import FloatingCommentsHelper from '../helpers/floating-comments.helper';
import { CommentStatus } from '../types/comment-status';
import ConversationCard from '../conversation-card/components/ConversationCard.vue';

export default defineComponent({
  name: 'FloatingComments',
  components: {
    ConversationCard,
  },
  props: {
    comments: {
      type: Array as PropType<Comment[]>,
      default: () => [],
    },
    currentUser: {
      type: Object as PropType<CurrentUser>,
      required: true,
    },
    selectedId: {
      type: String,
      default: '',
    },
    participants: {
      type: Array as PropType<Participant[]>,
      default: () => [],
    },
    readOnly: {
      type: Boolean,
    },
    dimensionsUpdating: {
      type: Boolean,
    },
    isRoomOwner: {
      type: Boolean,
    },
    allActiveParticipants: {
      type: Array as PropType<Participant[]>,
      default: () => [],
    },
  },
  emits: ['create-reply', 'select', 'update', 'delete', 'select', 'comments-repositioned'],
  setup(props, { emit }) {
    const commentsOffset: Ref<CommentOffset> = ref({});
    const floatingComments: Ref<HTMLElement> = ref(null);
    const minimumMargin = 10;

    const { commentsHeights, addHeight, removeHeight } = useCommentsHeightsCalculator();
    const { hoveredCommentId } = storeToRefs(useContextualCommentingStore());
    const { hoverComment } = useContextualCommentingStore();

    const getDesiredTop = (dataCommentId: string): number => {
      const anchor: HTMLElement = document.querySelector(`[data-comment-id="${dataCommentId}"]`);
      if (!anchor) {
        return 0;
      }

      const anchorTop = anchor.getBoundingClientRect().top;
      return anchorTop - floatingComments.value.getBoundingClientRect().top;
    };

    const setFirstCommentOffset = (commentId: string, margin: number) => {
      commentsOffset.value[commentId] = {
        desiredDistance: margin,
        actualMargin: margin,
        selectionMargin: margin,
      };
    };

    const setMinimumMargins = (commentId: string, desiredDistance: number) => {
      commentsOffset.value[commentId] = {
        desiredDistance,
        actualMargin: minimumMargin,
        selectionMargin: minimumMargin,
      };
    };

    const setNewMargins = (commentId: string, newMargin: number, desiredDistance: number) => {
      commentsOffset.value[commentId] = {
        desiredDistance,
        actualMargin: newMargin,
        selectionMargin: newMargin,
      };
    };

    const setMargins = () => {
      let occupiedSpace = 0;
      props.comments.forEach(({ id }: Comment, index: number) => {
        const desiredTop = getDesiredTop(id);

        if (index === 0) {
          setFirstCommentOffset(id, desiredTop);
          occupiedSpace = desiredTop + commentsHeights.value[id];
          return;
        }

        const newMargin = desiredTop - occupiedSpace;

        if (newMargin < minimumMargin) {
          setMinimumMargins(id, desiredTop);
          occupiedSpace += minimumMargin + commentsHeights.value[id];
          return;
        }

        setNewMargins(id, newMargin, desiredTop);
        occupiedSpace += newMargin + commentsHeights.value[id];
      });
    };

    const resetCommentsSelectionMargins = () =>
      Object.keys(commentsOffset.value).forEach(
        (id: string) =>
          (commentsOffset.value[id].selectionMargin = commentsOffset.value[id].actualMargin)
      );

    const getActualDistanceFromTop = (commentIds: string[], actualMargin: number) =>
      commentIds.reduce(
        (distance: number, id: string) =>
          distance + commentsHeights.value[id] + commentsOffset.value[id].actualMargin,
        0
      ) + actualMargin;

    const getSelectionDistanceFromTop = (commentIds: string[], selectionMargin: number) =>
      commentIds.reduce(
        (distance: number, id: string) =>
          distance + commentsHeights.value[id] + commentsOffset.value[id].selectionMargin,
        0
      ) + selectionMargin;

    const updateMarginForFollowingComments = (
      selectedCommentId: string,
      selectedCommentPositionChange: number
    ) => {
      const commentIds = FloatingCommentsHelper.getCommentIdsAfterSelectedComment(
        selectedCommentId,
        props.comments
      );
      if (!commentIds.length) {
        return;
      }
      commentIds.forEach((id: string) => {
        const previousCommentIds = FloatingCommentsHelper.getReversedCommentIdsBeforeId(
          id,
          props.comments
        );
        const combinedDistance = getSelectionDistanceFromTop(
          previousCommentIds,
          commentsOffset.value[id].actualMargin
        );
        const futureDistance = combinedDistance - selectedCommentPositionChange;
        if (futureDistance === commentsOffset.value[id].desiredDistance) {
          return;
        }
        if (
          !FloatingCommentsHelper.willCommentBePushedDown(
            futureDistance,
            commentsOffset.value[id].desiredDistance
          )
        ) {
          const marginAdditionToMaintainDesiredDistance =
            commentsOffset.value[id].desiredDistance - futureDistance;
          commentsOffset.value[id].selectionMargin += marginAdditionToMaintainDesiredDistance;
          return;
        }
        if (
          FloatingCommentsHelper.canDecreaseMargin(
            commentsOffset.value[id].selectionMargin,
            minimumMargin
          )
        ) {
          commentsOffset.value[id].selectionMargin = FloatingCommentsHelper.decreaseMargin(
            commentsOffset.value[id].selectionMargin,
            commentsOffset.value[id].desiredDistance,
            futureDistance,
            minimumMargin
          );
        }
      });
    };

    const adjustMarginsDuringSelection = () => {
      resetCommentsSelectionMargins();

      const selectedComment = commentsOffset.value[props.selectedId];
      if (!selectedComment) {
        return;
      }

      const { desiredDistance, actualMargin } = selectedComment;

      if (desiredDistance === actualMargin) {
        selectedComment.selectionMargin = desiredDistance;
      }

      const previousCommentIds = FloatingCommentsHelper.getReversedCommentIdsBeforeId(
        props.selectedId,
        props.comments
      );

      const distanceFromTop = getActualDistanceFromTop(previousCommentIds, actualMargin);
      const distanceToRemove = distanceFromTop - desiredDistance;

      updateMarginForFollowingComments(props.selectedId, distanceToRemove);

      if (previousCommentIds.length === 0) {
        return;
      }

      let distanceRemaining = distanceToRemove;
      previousCommentIds.forEach((id: string, index: number) => {
        if (distanceRemaining === 0) {
          return;
        }

        if (index + 1 === previousCommentIds.length) {
          commentsOffset.value[id].selectionMargin -= distanceRemaining;
          distanceRemaining = 0;
          return;
        }

        if (commentsOffset.value[id].actualMargin === minimumMargin) {
          return;
        }

        const allowedMarginToRemove = FloatingCommentsHelper.getMarginToRemoveFromComment(
          commentsOffset.value[id].actualMargin,
          distanceRemaining,
          minimumMargin
        );

        if (allowedMarginToRemove === 0) {
          return;
        }

        commentsOffset.value[id].selectionMargin -= allowedMarginToRemove;
        distanceRemaining -= allowedMarginToRemove;
      });
    };

    const resizeObserver = new ResizeObserver(entries => {
      entries.forEach((entry: ResizeObserverEntry) => {
        const { height } = entry.target.getBoundingClientRect();
        const commentId = entry.target.attributes['data-conversation-card-id'].value;
        addHeight(commentId, height);
      });
      setMargins();
      if (props.selectedId) {
        adjustMarginsDuringSelection();
      }
    });

    watch(
      () => props.dimensionsUpdating,
      async () => {
        if (!props.dimensionsUpdating) return;
        await nextTick();
        setMargins();
        if (props.selectedId) {
          adjustMarginsDuringSelection();
        }
        emit('comments-repositioned');
      }
    );

    const removeComments = (newComments: Comment[], oldComments: Comment[]) => {
      const removedComments = oldComments.filter(
        (comment: Comment) =>
          !newComments.some((newComment: Comment) => newComment.id === comment.id)
      );
      removedComments.forEach((comment: Comment) => {
        if (commentsOffset.value[comment.id]) {
          delete commentsOffset.value[comment.id];
        }
        const element = document.querySelector(`[data-conversation-card-id="${comment.id}"]`);
        if (!element) {
          return;
        }
        resizeObserver.unobserve(element);
        removeHeight(comment.id);
      });
    };

    const addComments = (newComments: Comment[], oldComments: Comment[]) => {
      const addedComments = newComments.filter(
        (comment: Comment) =>
          !oldComments.some((oldComment: Comment) => oldComment.id === comment.id)
      );
      addedComments.forEach((comment: Comment) => {
        const element = document.querySelector(`[data-conversation-card-id="${comment.id}"]`);
        if (!element) {
          return;
        }
        resizeObserver.observe(element);
      });
    };

    watch(
      () => props.comments,
      async (newComments, oldComments) => {
        if (
          newComments.length == oldComments.length &&
          FloatingCommentsHelper.idsUnchanged(newComments, oldComments)
        ) {
          return;
        }

        await nextTick();

        if (newComments.length > oldComments.length) {
          addComments(newComments, oldComments);
          return;
        }
        if (newComments.length < oldComments.length) {
          removeComments(newComments, oldComments);
          return;
        }

        // Remove and add comments in case of comment id diff - optimistic rendering
        removeComments(newComments, oldComments);
        addComments(newComments, oldComments);
      }
    );

    onMounted(() => {
      if (!props.comments.length) {
        return;
      }
      document
        .querySelectorAll('[data-conversation-card-id]')
        .forEach((element: Element) => resizeObserver.observe(element));
    });

    onBeforeUnmount(() => {
      resizeObserver.disconnect();
    });

    // Only handle selected comments that has status 'draft'. Other comment selection will be handle in resize observer when their height changes
    watch(
      () => props.selectedId,
      (id: string, previousId: string) => {
        const affectedId = id || previousId;
        const comment = props.comments.find(comment => comment.id === affectedId);
        const commentIsInDraft = comment?.status === CommentStatus.Draft;
        if (!comment || !commentIsInDraft) {
          return;
        }
        if (!id) {
          setMargins();
          return;
        }
        adjustMarginsDuringSelection();
      }
    );

    const getMarginTop = (commentId: string) => {
      if (!commentsOffset.value[commentId]) {
        return 0;
      }
      return props.selectedId
        ? commentsOffset.value[commentId].selectionMargin
        : commentsOffset.value[commentId].actualMargin;
    };

    const route = useRoute();

    const isSelected = (commentId: string) => {
      const { commentId: routeCommentId } = route.query;

      return props.selectedId === commentId || routeCommentId === commentId;
    };

    return {
      floatingComments,
      getMarginTop,
      hoverComment,
      hoveredCommentId,

      // Exposed for tests
      commentsHeights,
      commentsOffset,
      minimumMargin,
      setFirstCommentOffset,
      setMinimumMargins,
      setNewMargins,
      resetCommentsSelectionMargins,
      getActualDistanceFromTop,
      getSelectionDistanceFromTop,
      setMargins,
      getDesiredTop,
      updateMarginForFollowingComments,
      adjustMarginsDuringSelection,
      isSelected,
    };
  },
});
</script>
<style lang="scss" scoped>
.floating-comments-container {
  height: 100%;
  width: 100%;
  float: right;
}
</style>
